import React, { useEffect, useState, useMemo, useRef } from "react";
import {
    TextField, Box, Typography, Snackbar, SnackbarContent,
    IconButton, Chip,
} from "@material-ui/core";
import NoteService from "./Locality/Service/noteService";
import SchoolService from "./Locality/Service/schoolService";
import SocketService from "./Locality/Service/socketService";
import { makeStyles } from '@material-ui/core/styles';
import SendIcon from '@material-ui/icons/Send';
import Widget from "../../components/Widget/Widget";

const SCHOOL_ROLES = ['SCHOOLE', 'TEACHER', 'SUPERADMIN'];

const normalizeId = (value) => {
    if (value == null || value === '') return '';
    if (typeof value === 'string' || typeof value === 'number') {
        const s = String(value).trim();
        return s === '[object Object]' ? '' : s;
    }
    if (typeof value === 'object') {
        if (value.$oid) return String(value.$oid);
        if (value._id && value._id !== value) return normalizeId(value._id);
        if (value.id && value.id !== value) return normalizeId(value.id);
        if (typeof value.toHexString === 'function') return value.toHexString();
        const s = typeof value.toString === 'function' ? value.toString() : '';
        if (s && s !== '[object Object]') return s;
    }
    return '';
};

const inferRoleFromCreatedBy = (createdBy) => {
    if (!createdBy || typeof createdBy !== 'object') return '';
    if (Array.isArray(createdBy.students) && createdBy.students.length > 0) return 'PARENT';
    if (createdBy.teacher && (createdBy.teacher._id || createdBy.teacher.teacherName)) return 'TEACHER';
    if (createdBy.school && (createdBy.school._id || createdBy.school.schooleName)) return 'SCHOOLE';
    return '';
};

const getMyUserId = (userDetails) => (
    normalizeId(userDetails?._id)
    || normalizeId(userDetails?.id)
    || normalizeId(userDetails?.userId)
);

const getSenderRole = (msg) => {
    const direct = String(msg?.senderRole || msg?.createdBy?.role || '').toUpperCase();
    if (direct === 'PARENT' || SCHOOL_ROLES.includes(direct)) return direct;
    return inferRoleFromCreatedBy(msg?.createdBy);
};

const isOwnMessage = (msg, userDetails) => {
    const myRole = String(userDetails?.role || '').toUpperCase();
    const senderRole = getSenderRole(msg);
    const iAmSchool = SCHOOL_ROLES.includes(myRole);
    const senderIsSchool = SCHOOL_ROLES.includes(senderRole);
    const senderIsParent = senderRole === 'PARENT';

    // Parent screen: school/teacher messages always sit on the received (left) side
    if (myRole === 'PARENT') {
        return senderIsParent;
    }
    if (iAmSchool && (senderIsParent || senderIsSchool)) {
        return senderIsSchool;
    }

    const me = getMyUserId(userDetails);
    const senderId = normalizeId(msg?.senderId) || normalizeId(msg?.createdBy);
    return Boolean(me && senderId && me === senderId);
};

const getSenderLabel = (msg) => {
    const role = getSenderRole(msg);
    const createdBy = msg?.createdBy;
    if (role === 'TEACHER') return createdBy?.teacher?.teacherName || 'Teacher';
    if (role === 'SCHOOLE' || role === 'SUPERADMIN') {
        return createdBy?.school?.schooleName || 'School';
    }
    if (role === 'PARENT') return 'Parent';
    return '';
};

const useStyles = makeStyles((theme) => ({
    yellowHeader: {
        backgroundColor: '#FFC107',
        color: 'white',
        padding: '16px 24px',
        marginBottom: '24px',
        borderRadius: '4px 4px 0 0',
    },
    notesListContainer: {
        maxWidth: '800px',
        margin: '0 auto',
    },
    notesWidgetRoot: {
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 'min(720px, calc(100vh - 200px))',
        overflow: 'hidden',
        [theme.breakpoints.down('xs')]: {
            maxHeight: 'calc(100vh - 160px)',
        },
    },
    notesListHeader: {
        flexShrink: 0,
        padding: theme.spacing(2, 2, 1, 2),
    },
    chatMessages: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        padding: theme.spacing(2),
        backgroundColor: '#ece5dd',
        display: 'flex',
        flexDirection: 'column',
    },
    chatMessageRow: {
        display: 'flex',
        width: '100%',
        marginBottom: 8,
    },
    chatMessageRowSent: {
        justifyContent: 'flex-end',
    },
    chatMessageRowReceived: {
        justifyContent: 'flex-start',
    },
    chatMessageBubble: {
        padding: '8px 12px 6px',
        borderRadius: 8,
        maxWidth: '75%',
        wordWrap: 'break-word',
        boxShadow: '0 1px 1px rgba(0,0,0,0.08)',
    },
    messageSent: {
        backgroundColor: '#dcf8c6',
        color: '#111',
        borderTopRightRadius: 0,
    },
    messageReceived: {
        backgroundColor: '#ffffff',
        color: '#111',
        borderTopLeftRadius: 0,
    },
    senderName: {
        display: 'block',
        fontWeight: 700,
        fontSize: '0.72rem',
        marginBottom: 2,
        opacity: 0.85,
    },
    messageTime: {
        display: 'block',
        marginTop: 4,
        opacity: 0.65,
        fontSize: '0.68rem',
        textAlign: 'right',
    },
    chatComposer: {
        flexShrink: 0,
        padding: 12,
        borderTop: '1px solid #e0e0e0',
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        backgroundColor: '#f0f0f0',
    },
}));

export default function Note() {
    const classes = useStyles();
    const userDetails = useMemo(() => JSON.parse(localStorage.getItem("userDetail") || "{}"), []);
    const studentId = userDetails?.studentId || null;
    const [schoolName, setSchoolName] = useState("");
    const [toast, setToast] = useState({
        open: false,
        type: 'success',
        message: '',
    });
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const chatMessagesEndRef = useRef(null);
    const [unreadCounts, setUnreadCounts] = useState({});
    const totalUnreadChats = useMemo(
        () => Object.values(unreadCounts).reduce((a, v) => a + (Number(v) || 0), 0),
        [unreadCounts],
    );
    const unreadCountsRef = useRef({});
    const activeRootNoteIdRef = useRef(null);
    const socketInitializedRef = useRef(false);

    const applyUnreadSummaryData = (res) => {
        const data = res?.data || res || {};
        const normalized = {};
        Object.entries(data.byNoteId || {}).forEach(([noteId, count]) => {
            const n = Number(count) || 0;
            if (n > 0) normalized[String(noteId)] = n;
        });
        setUnreadCounts(normalized);
    };

    const clearUnreadForNote = (noteId) => {
        const id = String(noteId);
        setUnreadCounts((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
    };

    const persistNoteRead = async (noteId) => {
        if (!noteId) return;
        try {
            await NoteService.markNoteRead(String(noteId));
        } catch (err) {
            console.error('Error marking note as read:', err);
        }
    };

    const markNotesAsRead = async (noteIds) => {
        const ids = (noteIds || []).map((id) => String(id)).filter(Boolean);
        if (ids.length === 0) return;
        ids.forEach((noteId) => clearUnreadForNote(noteId));
        await Promise.all(ids.map((noteId) => persistNoteRead(noteId)));
    };

    const scrollChatToBottom = () => {
        setTimeout(() => {
            if (chatMessagesEndRef.current) {
                chatMessagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
            }
        }, 100);
    };

    const leaveActiveChatRoom = () => {
        if (activeRootNoteIdRef.current) {
            SocketService.leaveNoteChat(activeRootNoteIdRef.current);
            activeRootNoteIdRef.current = null;
        }
    };

    const resolveRootNoteId = (messages) => {
        const list = Array.isArray(messages) ? messages : [];
        const roots = list.filter((m) => {
            const kind = String(m?.messageKind || 'NOTE').toUpperCase();
            return kind !== 'CHAT' && !m?.parentNoteId;
        });
        if (roots.length > 0) return String(roots[roots.length - 1]._id);
        for (let i = list.length - 1; i >= 0; i -= 1) {
            const parent = list[i]?.parentNoteId;
            if (parent) return String(parent._id || parent);
        }
        if (list.length > 0 && list[0]?._id) return String(list[0]._id);
        return null;
    };

    const appendChatMessage = (messageData) => {
        if (!messageData) return;
        const normalized = {
            ...messageData,
            note: messageData.note || messageData.message,
            senderId: messageData.senderId || messageData.createdBy?._id || messageData.createdBy,
            senderRole: messageData.senderRole
                || messageData.createdBy?.role
                || inferRoleFromCreatedBy(messageData.createdBy),
        };
        setChatMessages((prev) => {
            const incomingId = String(normalized._id || '');
            if (incomingId && !incomingId.startsWith('temp-') && prev.some((msg) => String(msg._id) === incomingId)) {
                return prev;
            }

            const incomingNote = String(normalized.note || '').trim();
            const withoutTemp = prev.filter((msg) => {
                if (!String(msg._id || '').startsWith('temp-')) return true;
                return String(msg.note || '').trim() !== incomingNote;
            });
            return [...withoutTemp, normalized];
        });
        scrollChatToBottom();
    };

    const joinRootNoteRoom = (rootNoteId) => {
        if (!rootNoteId) return;
        if (activeRootNoteIdRef.current && activeRootNoteIdRef.current !== rootNoteId) {
            SocketService.leaveNoteChat(activeRootNoteIdRef.current);
        }
        activeRootNoteIdRef.current = rootNoteId;
        SocketService.joinNoteChat(rootNoteId, {
            onMessage: (messageData) => {
                appendChatMessage(messageData);
                if (messageData?.noteId || messageData?.parentNoteId || rootNoteId) {
                    persistNoteRead(messageData?.noteId || messageData?.parentNoteId || rootNoteId);
                }
            },
            onError: (error) => {
                console.error('Socket error:', error);
            },
        });
    };

    const loadMergedChat = async ({ markRead = false } = {}) => {
        try {
            if (!studentId) {
                setChatMessages([]);
                return;
            }
            const res = await NoteService.getChatMessages(studentId);
            const raw = Array.isArray(res) ? res : res?.data || [];
            const messages = raw.map((m) => ({
                ...m,
                senderId: m.senderId || m.createdBy?._id || m.createdBy,
                senderRole: m.senderRole || m.createdBy?.role || inferRoleFromCreatedBy(m.createdBy),
            }));
            setChatMessages(messages);

            if (markRead) {
                const roots = messages.filter((m) => {
                    const kind = String(m?.messageKind || 'NOTE').toUpperCase();
                    return kind !== 'CHAT' && !m?.parentNoteId;
                });
                const unreadIds = Object.keys(unreadCountsRef.current || {});
                const rootIds = roots.map((n) => n._id).filter(Boolean);
                await markNotesAsRead([...new Set([...unreadIds, ...rootIds.map(String)])]);
            }

            joinRootNoteRoom(resolveRootNoteId(messages));
            scrollChatToBottom();
        } catch (err) {
            console.error('Error loading chat:', err);
            setChatMessages([]);
        }
    };

    const handleUnreadUpdate = (payload) => {
        const noteId = payload?.noteId ? String(payload.noteId) : '';
        if (!noteId) return;

        // Parent always views the merged thread — mark read and refresh
        persistNoteRead(noteId);
        loadMergedChat({ markRead: true });
    };

    const handleNewNote = (payload) => {
        if (!payload?.noteId) return;
        const role = String(userDetails?.role || '').toUpperCase();
        if (role !== 'PARENT') return;

        const myStudentId = studentId ? String(studentId) : '';
        const payloadStudentId = payload?.studentId ? String(payload.studentId) : '';
        if (myStudentId && payloadStudentId && myStudentId !== payloadStudentId) return;

        const senderRole = String(payload.senderRole || '').toUpperCase();
        if (senderRole === 'PARENT') return;

        loadMergedChat({ markRead: true });

        const title = payload.notificationTitle || 'New message from school';
        const body = payload.notificationBody || payload.preview || payload.noteType || '';
        setToast({
            open: true,
            type: 'success',
            message: body ? `${title} — ${body}` : title,
        });
    };

    useEffect(() => {
        unreadCountsRef.current = unreadCounts;
    }, [unreadCounts]);

    useEffect(() => {
        if (userDetails && (userDetails.role === 'SCHOOLE' || userDetails.role === 'TEACHER' || userDetails.role === 'PARENT')) {
            const schooleId = userDetails.schooleId || userDetails.schoolId;
            if (schooleId) {
                SchoolService.getSchooleRegistrationById(schooleId)
                    .then((res) => {
                        if (res && res.schooleName) {
                            setSchoolName(res.schooleName);
                        }
                    })
                    .catch((err) => {
                        console.error('Error fetching school details:', err);
                    });
            }
        }

        if (!socketInitializedRef.current) {
            SocketService.initializeSocket();
            socketInitializedRef.current = true;
        }

        NoteService.getUnreadSummary()
            .then(applyUnreadSummaryData)
            .catch((err) => console.error('Error fetching unread summary:', err));

        loadMergedChat({ markRead: true });

        const offUpdate = SocketService.onUnreadUpdate(handleUnreadUpdate);
        const offCleared = SocketService.onUnreadCleared((payload) => {
            if (!payload?.noteId) return;
            clearUnreadForNote(payload.noteId);
        });
        const offNewNote = SocketService.onNewNote(handleNewNote);

        return () => {
            if (typeof offUpdate === 'function') offUpdate();
            if (typeof offCleared === 'function') offCleared();
            if (typeof offNewNote === 'function') offNewNote();
            leaveActiveChatRoom();
            socketInitializedRef.current = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSendMessage = async () => {
        try {
            const text = (chatInput || '').trim();
            if (!text) return;
            if (!studentId) return;

            setChatInput('');
            const rootId = resolveRootNoteId(chatMessages) || activeRootNoteIdRef.current;
            const myRole = String(userDetails?.role || '').toUpperCase();
            const myId = userDetails?._id || userDetails?.id || '';

            const appendOwnMessage = () => {
                appendChatMessage({
                    _id: `temp-${Date.now()}`,
                    note: text,
                    messageKind: 'CHAT',
                    senderRole: myRole,
                    senderId: myId,
                    createdBy: {
                        _id: myId,
                        role: myRole,
                        teacher: myRole === 'TEACHER'
                            ? { teacherName: userDetails?.userName || 'Teacher' }
                            : null,
                        school: myRole === 'SCHOOLE' || myRole === 'SUPERADMIN'
                            ? { schooleName: schoolName || 'School' }
                            : null,
                        students: myRole === 'PARENT' ? [{ firstName: 'Parent' }] : [],
                    },
                    createdAt: new Date().toISOString(),
                    parentNoteId: rootId,
                });
            };

            if (!rootId) {
                await NoteService.sendMergedChat({
                    studentId,
                    note: text,
                    noteType: 'Note',
                });
                await loadMergedChat({ markRead: true });
                return;
            }

            appendOwnMessage();
            await sendViaRestAPI(text, rootId);
        } catch (err) {
            console.error('Error sending message:', err);
            setToast({
                open: true,
                type: 'error',
                message: err.response?.data?.message || 'Error sending message',
            });
        }
    };

    const sendViaRestAPI = async (text, rootId) => {
        try {
            await NoteService.sendMergedChat({
                studentId,
                note: text,
                parentNoteId: rootId,
            });
            await loadMergedChat({ markRead: true });
        } catch (err) {
            console.error('Error sending via REST API:', err);
            setToast({
                open: true,
                type: 'error',
                message: err.response?.data?.message || 'Error sending message',
            });
        }
    };

    const formatTimeShort = (dateString) => {
        if (!dateString) return '';
        const d = new Date(dateString);
        if (Number.isNaN(d.getTime())) return '';
        return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    };

    const isMine = (msg) => isOwnMessage(msg, userDetails);

    return (
        <>
            <div className={classes.yellowHeader}>
                <Typography variant="h5" style={{ fontWeight: 'bold', color: 'white' }}>
                    {schoolName ? `  ${schoolName}` : ''}
                </Typography>
            </div>

            <Box className={classes.notesListContainer}>
                <Widget>
                    <Box className={classes.notesWidgetRoot}>
                        <Box className={classes.notesListHeader}>
                            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gridGap={12}>
                                <Box display="flex" alignItems="center" flexWrap="wrap" gridGap={10}>
                                    <Typography variant="h6" style={{ fontWeight: 700 }}>
                                        Chat with School
                                    </Typography>
                                    {totalUnreadChats > 0 && (
                                        <Chip
                                            size="small"
                                            label={`${totalUnreadChats > 99 ? '99+' : totalUnreadChats} new`}
                                            style={{
                                                backgroundColor: '#e53935',
                                                color: '#fff',
                                                fontWeight: 700,
                                                height: 22,
                                            }}
                                        />
                                    )}
                                </Box>
                            </Box>
                        </Box>

                        <Box className={classes.chatMessages} role="region" aria-label="Chat with school">
                            {chatMessages.length === 0 ? (
                                <Box style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                                    <Typography variant="body2">
                                        No messages yet. Type below to start a conversation with the school.
                                    </Typography>
                                </Box>
                            ) : (
                                <>
                                    {chatMessages.map((m, idx) => {
                                        const isFormNote =
                                            m.noteType && (m.messageKind !== 'CHAT' || !m.messageKind);
                                        const mine = isMine(m);
                                        const senderLabel = getSenderLabel(m);
                                        return (
                                            <Box
                                                key={m._id || idx}
                                                className={`${classes.chatMessageRow} ${
                                                    mine
                                                        ? classes.chatMessageRowSent
                                                        : classes.chatMessageRowReceived
                                                }`}
                                                style={{
                                                    display: 'flex',
                                                    width: '100%',
                                                    justifyContent: mine ? 'flex-end' : 'flex-start',
                                                    marginBottom: 8,
                                                }}
                                            >
                                                <Box
                                                    className={`${classes.chatMessageBubble} ${
                                                        mine ? classes.messageSent : classes.messageReceived
                                                    }`}
                                                    style={{
                                                        backgroundColor: isFormNote
                                                            ? '#fff3cd'
                                                            : mine
                                                                ? '#dcf8c6'
                                                                : '#ffffff',
                                                        color: isFormNote ? '#856404' : '#111',
                                                        border: isFormNote ? '1px solid #ffc107' : undefined,
                                                        borderTopRightRadius: mine ? 0 : 8,
                                                        borderTopLeftRadius: mine ? 8 : 0,
                                                    }}
                                                >
                                                    {!mine && senderLabel && (
                                                        <Typography
                                                            variant="caption"
                                                            className={classes.senderName}
                                                            style={{ color: isFormNote ? '#856404' : '#075e54' }}
                                                        >
                                                            {senderLabel}
                                                        </Typography>
                                                    )}
                                                    {isFormNote && (
                                                        <Box style={{ marginBottom: 4 }}>
                                                            <Typography
                                                                variant="caption"
                                                                style={{
                                                                    fontWeight: 700,
                                                                    fontSize: '0.7rem',
                                                                    opacity: 0.9,
                                                                }}
                                                            >
                                                                📋 {m.noteType}
                                                            </Typography>
                                                        </Box>
                                                    )}
                                                    <Typography variant="body2">{m.note || '-'}</Typography>
                                                    <Typography variant="caption" className={classes.messageTime}>
                                                        {formatTimeShort(m.createdAt)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                    <div ref={chatMessagesEndRef} />
                                </>
                            )}
                        </Box>

                        <Box className={classes.chatComposer}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                placeholder="Type a message..."
                                value={chatInput}
                                onChange={(e) => setChatInput(e.target.value)}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey && chatInput.trim()) {
                                        e.preventDefault();
                                        handleSendMessage();
                                    }
                                }}
                                size="small"
                                style={{ backgroundColor: '#fff' }}
                                disabled={!studentId}
                            />
                            <IconButton
                                color="primary"
                                onClick={handleSendMessage}
                                disabled={!chatInput.trim() || !studentId}
                            >
                                <SendIcon />
                            </IconButton>
                        </Box>
                    </Box>
                </Widget>
            </Box>

            <Snackbar
                open={toast.open}
                autoHideDuration={3000}
                onClose={() => setToast((prev) => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <SnackbarContent
                    style={{
                        backgroundColor: toast.type === 'success' ? '#2e7d32' : '#d32f2f',
                        color: '#fff',
                        fontWeight: 600,
                    }}
                    message={toast.message}
                />
            </Snackbar>
        </>
    );
}
